import { type JSX, type ReactNode } from 'react';
import { roleCanAccessCop } from '@tsr/shared-types';
import { useAuth } from './useAuth';
import { LoginView } from './LoginView';
import { OperatorModeShell } from '../OperatorModeShell';
import { BootSplash } from '../BootSplash';
import { isDemoMode } from '../demoMode';

/**
 * Root login gate (RBAC R.2b, #314). Wraps the COP outermost so a deep-link URL
 * (e.g. `/#cam=<id>`) survives a post-login render — the gate swaps the view in
 * place without navigating, so the hash/query is preserved for <App> to read once
 * the operator signs in.
 *
 * `bypass` is set for display/capability contexts (the popout viewer, the video
 * wall, the token-gated Briefing-Up view, the second-monitor ops panel) — those
 * must NEVER be blocked by an interactive login, so they render untouched.
 *
 * Otherwise:
 *   - status==='loading' → a lightweight splash (no COP flash before the probe).
 *   - authEnforced && !user → the login screen.
 *   - user is a NON-COP principal (an external drone-operator; Drone D1 #316) → the
 *     stripped-down Operator-Mode shell, never the COP (the SERVER class gate is the
 *     real wall — this is its cosmetic counterpart).
 *   - else → the children (the COP — real user identity when present, the demo
 *     identity when not).
 *
 * With AUTH_ENFORCED shipped OFF, config returns `{ authEnforced: false }`, so the
 * gate always renders children ⇒ NON-BREAKING. R.2c flipping the server flag makes
 * the gate enforce automatically — no web change needed.
 *
 * Demo gate-skip (RBAC R.2c, #314): a demo build/URL must never hit the login wall,
 * even when enforcement is on. This is CLIENT-SIDE COSMETIC ONLY — it suppresses the
 * local login screen so a sales/trial demo renders the COP. It deliberately sends NO
 * "I'm demo" signal to the server: the API's AUTH_ENFORCED env flag remains the SOLE
 * enforcement authority. A client-spoofable demo header would be an auth bypass, so
 * the demo path here only affects what the browser renders, never what the API trusts
 * (a demo build simply points at an API with AUTH_ENFORCED off).
 */
export function AuthGate({
  bypass = false,
  children,
}: {
  bypass?: boolean;
  children: ReactNode;
}): JSX.Element {
  const { status, authEnforced, user } = useAuth();

  if (bypass) return <>{children}</>;

  // Demo gate-skip — render the COP, never the login screen (client-cosmetic; see
  // the doc-comment above). Resolved live so a `?demo=1` URL or the sticky toggle
  // takes effect without a reload.
  if (isDemoMode()) return <>{children}</>;

  if (status === 'loading') {
    // #259 — the auth-probe splash shares the BootSplash with the globe-loading
    // chrome so the cold boot (probe → COP mounts → globe imagery) reads as one
    // continuous branded screen. A distinct line per stage ("Connecting…" then
    // "Assembling the operating picture…") makes the two phases read as a
    // progression, not a duplicated status announcement. Cosmetic only — no auth
    // logic changes; the `auth-splash` testid is preserved for the gate tests.
    return <BootSplash testId="auth-splash" line="Connecting…" />;
  }

  if (authEnforced && !user) return <LoginView />;

  // External-operator (non-COP) principal → the stripped-down Operator Mode shell,
  // NEVER the COP (Drone D1 #316). This fires regardless of AUTH_ENFORCED — a real
  // drone-operator can hold a session while the flag is off, exactly as the SERVER
  // class gate is unconditional. The server gate is the actual wall; this is its
  // cosmetic web counterpart (a COP user / the demo identity fall through to the COP).
  if (user && !roleCanAccessCop(user.role)) return <OperatorModeShell />;

  return <>{children}</>;
}
